import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDriverApp } from './DriverAppProvider';

const OtpVerificationScreen = ({ onVerified }) => {
  const { user, theme, sendOtp, verifyOtp } = useDriverApp();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  const isDark = theme === 'dark';
  const bg = isDark ? 'bg-black' : 'bg-white';
  const text = isDark ? 'text-white' : 'text-black';
  const subtext = isDark ? 'text-white/60' : 'text-black/60';
  const border = isDark ? 'border-[#262626]' : 'border-[#e5e5e5]';
  const inputBg = isDark ? 'bg-[#0a0a0a] border-[#262626] text-white' : 'bg-white border-[#e5e5e5] text-black';

  const phone = user?.phone || '';
  const maskedPhone = phone.length >= 4
    ? '••• ••• ' + phone.slice(-4)
    : 'your phone number';

  const startCooldown = useCallback((seconds = 60) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    startCooldown(60);
    inputRefs.current[0]?.focus();
    return () => clearInterval(timerRef.current);
  }, [startCooldown]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);
    setError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newDigits = [...Array(6)].map((_, i) => pasted[i] || '');
    setDigits(newDigits);
    setError('');
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const code = digits.join('');
    if (code.length < 6) {
      setError('Enter the full 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await verifyOtp(code);
      onVerified();
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError('');
    try {
      await sendOtp();
      startCooldown(60);
    } catch {
      setError('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col font-['Oxanium'] ${bg}`}>
      {/* Header */}
      <div className={`pt-16 pb-10 px-6 text-center border-b ${border}`}>
        <div className="w-20 h-20 bg-red-600 flex items-center justify-center mx-auto mb-5">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <h1 className={`text-2xl font-bold tracking-wider ${text}`}>VERIFY PHONE</h1>
        <p className={`mt-2 text-sm ${subtext}`}>
          Enter the 6-digit code sent to{' '}
          <span className={`font-semibold ${text}`}>{maskedPhone}</span>
        </p>
      </div>

      {/* OTP input */}
      <div className="flex-1 px-6 py-10">
        {error && (
          <div className="bg-red-600/20 border border-red-600/50 p-3 mb-6">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {/* 6 digit boxes */}
        <div className="flex gap-2 justify-center mb-8" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              className={`w-12 h-14 text-center text-xl font-bold border-2 focus:outline-none focus:border-red-600 transition-colors ${inputBg} ${
                digit ? 'border-red-600' : isDark ? 'border-[#262626]' : 'border-[#e5e5e5]'
              }`}
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          disabled={loading || digits.join('').length < 6}
          className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/40 text-white font-semibold py-4 tracking-wider transition-colors"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              VERIFYING...
            </span>
          ) : 'VERIFY CODE'}
        </button>

        {/* Resend */}
        <div className="text-center mt-6">
          {cooldown > 0 ? (
            <p className={`text-sm ${subtext}`}>
              Resend code in{' '}
              <span className={`font-semibold ${text}`}>{cooldown}s</span>
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-red-500 hover:text-red-400 text-sm font-medium disabled:opacity-50"
            >
              {resending ? 'SENDING...' : 'RESEND CODE'}
            </button>
          )}
        </div>

        <p className={`text-xs text-center mt-8 ${subtext}`}>
          Check your SMS messages. The code expires in 10 minutes.
        </p>
      </div>
    </div>
  );
};

export default OtpVerificationScreen;
